import * as yup from 'yup';

import { isCompleteScheduleDate, isRealUkTime, isSlotInFuture } from 'app/utils/scheduledGifts';

export const rewardPointsSchema = yup.object().shape({
  pointsAmount: yup
    .number()
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .required('Please enter the amount')
    .integer('The amount should be a whole number (no decimals)')
    .min(1, 'The amount must be at least 1 point')
    .max(20000000, 'Maximum points amount is 20,000,000 in 24 hours.'),
  transactionReason: yup.string().required('Please enter the reason'),
  notes: yup.string().max(500, 'The notification reason text cannot exceed 500 characters'),
});

export const bulkRewardEmployeesFormSchema = yup.object().shape({
  pointsAmount: yup
    .number()
    .typeError('Points must be a number')
    .required('Points are required')
    .min(1, 'Points must be at least 1')
    .max(20000000, 'Points must be at most 20,000,000 in 24 hours')
    .test(
      'max-balance',
      'Insufficient balance. This amount exceeds the current points balance.',
      // eslint-disable-next-line func-names
      function (value: number) {
        // A scheduled gift is paid when it's sent, so the balance can be topped up before then.
        if (this.parent.sendMode === 'SCHEDULE') {
          return true;
        }

        const { availablePoints, selectedEmployeeCount } = this.options.context || {};

        // Skip validation only when context values are truly missing (null/undefined)
        if (selectedEmployeeCount == null || availablePoints == null) {
          return true;
        }

        // If value is not a finite number, defer to Yup's other numeric validations
        if (!Number.isFinite(value)) {
          return true;
        }

        const totalNeeded = value * selectedEmployeeCount;
        return totalNeeded <= availablePoints;
      }
    ),
  transactionReason: yup.string().required('Notification reason is required'),
  customMessage: yup
    .string()
    .required('Custom message is required')
    .max(130, 'Custom message cannot exceed 130 characters'),
  sendMode: yup.string().oneOf(['NOW', 'SCHEDULE']).required(),
  scheduleDate: yup.string().when('sendMode', {
    is: 'SCHEDULE',
    then: (schema) =>
      schema
        .required('Please select a date')
        .test('complete', 'Please enter a date as DD/MM/YYYY', (value) =>
          isCompleteScheduleDate(value)
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  scheduleTime: yup.string().when('sendMode', {
    is: 'SCHEDULE',
    then: (schema) =>
      schema
        .required('Please select a time')
        .test('in-future', 'Please select a time in the future', (value, context) =>
          isSlotInFuture(context.parent.scheduleDate, value || '')
        )
        .test(
          'real-uk-time',
          "That time doesn't happen in the UK on this day, as the clocks go forward. Please select another.",
          (value, context) => isRealUkTime(context.parent.scheduleDate, value || '')
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  postAsRecognition: yup.boolean(),
});
